import { Spin } from 'antd';
import { useTranslation } from 'react-i18next';

import { AccessDenied } from '@repo/ui/access-denied';

import { useCurrentTenant } from '@/hooks/use-current-tenant';
import { useCurrentUser } from '@/hooks/use-current-user';
import { useAuth } from '@/lib/auth';

import { FullPageError } from './full-page-error';

interface CriticalErrorBoundaryProps {
  children: React.ReactNode;
}

function formatErrorDetails(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const err = error as Record<string, unknown>;
  if ('status' in err) {
    const status = err.status;
    const data = err.data;
    const message =
      typeof data === 'object' && data && 'message' in data
        ? (data as { message: string }).message
        : JSON.stringify(data);
    return `Status ${status}: ${message}`;
  }
  if ('message' in err) return String(err.message);
  return JSON.stringify(error);
}

function isForbiddenError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    (error as { status: number }).status === 403
  );
}

/**
 * Wrapper component that checks for critical API errors during app initialization.
 * Shows a full-page error if subscription or user APIs fail.
 * Shows an access denied screen if the user lacks required roles (403).
 */
export function CriticalErrorBoundary({
  children,
}: CriticalErrorBoundaryProps) {
  const {
    isError: tenantError,
    error: tenantErrorObj,
    isLoading: tenantLoading,
    refetch: refetchTenant,
  } = useCurrentTenant();
  const {
    isError: userError,
    error: userErrorObj,
    isLoading: userLoading,
    refetch: refetchUser,
  } = useCurrentUser();
  const { logout } = useAuth();
  const { t } = useTranslation();

  // Show loading while initializing
  if (tenantLoading || userLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  // Show access denied if either API returned 403
  if (
    (tenantError && isForbiddenError(tenantErrorObj)) ||
    (userError && isForbiddenError(userErrorObj))
  ) {
    return (
      <AccessDenied
        title={t('errors.accessDenied.title')}
        message={t('errors.accessDenied.message')}
        onLogout={logout}
      />
    );
  }

  // Show error with details if critical APIs failed
  if (tenantError || userError) {
    const details: string[] = [];
    if (tenantError)
      details.push(`Subscription: ${formatErrorDetails(tenantErrorObj)}`);
    if (userError) details.push(`User: ${formatErrorDetails(userErrorObj)}`);

    return (
      <FullPageError
        title={t('errors.unableToLoad.title')}
        message={`${t('errors.unableToLoad.message')}\n\n${details.join('\n')}`}
        onRetry={() => {
          refetchTenant();
          refetchUser();
        }}
      />
    );
  }

  return <>{children}</>;
}
